import { CheckAreas } from './types';

/**
 * Every screen, tab and panel that holds settings.
 *
 * Permissions are the raw CASL claim strings from src/permissions/ability.ts. They are written as
 * literals rather than imported so this registry stays free of runtime dependencies (build scripts
 * import it directly); scripts/checkSettingIds.js validates every string here against ability.ts.
 *
 * `:instance` in a route is replaced with the chosen instance id by the deep-link resolver.
 */
export const AREAS = [
    // ---------------------------------------------------------------- organization
    {
        key: 'organization.basic',
        label: 'Organization · Basic Settings',
        route: '/settings/organization',
        permission: 'orgModify',
        tab: 'basic',
    },
    {
        key: 'organization.address',
        label: 'Organization · Address',
        route: '/settings/organization',
        permission: 'orgModify',
        tab: 'address',
    },
    {
        key: 'organization.advanced',
        label: 'Organization · Advanced Settings',
        route: '/settings/organization',
        permission: 'orgModify',
        tab: 'advanced',
    },
    {
        key: 'search.settings',
        label: 'Search Settings',
        route: '/settings/search',
    },
    {
        // The search results settings live in a panel on the search screen itself, not under
        // /settings/search - that page holds a different, smaller set of options.
        key: 'search.resultsPanel',
        label: 'Search · Results Settings',
        route: '/search',
        panel: { key: 'searchSettings' },
    },
    {
        key: 'categories',
        label: 'Category Management',
        route: '/settings/categories',
    },
    {
        key: 'procurement',
        label: 'Procurement Settings',
        route: '/settings/procurement',
        permission: 'procurement',
    },
    {
        key: 'reporting.apiUsage',
        label: 'API Usage Reporting',
        route: '/settings/reporting/api',
    },

    // ---------------------------------------------------------------- users
    {
        key: 'myUser',
        label: 'My User',
        route: '/settings/myUser',
    },
    {
        key: 'users.list',
        label: 'User Management',
        route: '/settings/users',
    },

    // ---------------------------------------------------------------- catalog & vendors
    {
        key: 'catalogs.list',
        label: 'Catalog Management',
        route: '/settings/catalogs',
        permission: 'catalogModify',
    },
    {
        key: 'catalogs.details',
        label: 'Catalog Management · Catalog',
        route: '/settings/catalogs',
        permission: 'catalogModify',
        instanceType: 'catalog',
        panel: { key: 'catalog' },
    },
    {
        key: 'vendors.list',
        label: 'Vendor Management',
        route: '/settings/vendors',
        permission: 'orgModify',
    },
    {
        key: 'vendors.details',
        label: 'Vendor Management · Vendor',
        route: '/settings/vendors',
        permission: 'orgModify',
        instanceType: 'vendor',
        panel: { key: 'vendor' },
    },
    {
        key: 'taxCodes.list',
        label: 'Tax Code Management',
        route: '/settings/taxcodes',
        permission: 'taxCodeModify',
    },
    {
        key: 'taxCodes.details',
        label: 'Tax Code Management · Tax Code',
        route: '/settings/taxcodes',
        permission: 'taxCodeModify',
        instanceType: 'taxCode',
        panel: { key: 'taxCode' },
    },
    {
        key: 'subOrganizations.list',
        label: 'Sub Organization Management',
        route: '/settings/subOrganizations',
    },
    {
        key: 'subOrganizations.details',
        label: 'Sub Organization Management · Sub Organization',
        route: '/settings/subOrganizations',
        instanceType: 'subOrganization',
        panel: { key: 'subOrganization' },
    },

    // ---------------------------------------------------------------- product bundles
    {
        // Bundles have no route of their own - the editor is a modal hosted above the router
        // (BundleModalHost) and can be opened from any page via ?panel=bundle&instance=<id>. There is
        // no bundle list query wired up for an instance picker yet, so unlike the panels above this
        // one has no instanceType: a generic link with no instance cannot resolve to a specific
        // bundle. A working link is produced by opening the bundle's editor and using Copy Link,
        // which carries the instance id, same as the per-section style editors.
        key: 'bundles.questions',
        label: 'Product Bundle · Questions',
        route: '/',
        panel: { key: 'bundle', tab: 'questions' },
        description: "The bundle editor's Questions tab, opened from wherever a bundle can be edited.",
    },

    // ---------------------------------------------------------------- contacts
    {
        key: 'contacts.companies',
        label: 'Contact Management · Companies',
        route: '/settings/contacts',
        tab: 'companies',
    },
    {
        key: 'contacts.contacts',
        label: 'Contact Management · Contacts',
        route: '/settings/contacts',
        tab: 'contacts',
    },
    {
        key: 'contacts.company',
        label: 'Contact Management · Company Details',
        route: '/settings/contacts',
        tab: 'companies',
        instanceType: 'company',
        panel: { key: 'company' },
    },
    {
        /*
         * Locations are edited in a panel nested inside the company panel, and the URL has only one
         * panel slot — there is nowhere to name which location. So this links to the tab, not to a
         * field on it, the same call made for the localization pages. A field id here would resolve
         * and then fail to highlight, which is worse than landing one click away.
         */
        key: 'contacts.company.locations',
        label: 'Contact Management · Company Locations',
        route: '/settings/contacts',
        tab: 'companies',
        instanceType: 'company',
        panel: { key: 'company', tab: 'locations' },
    },
    {
        key: 'contacts.contact',
        label: 'Contact Management · Contact Details',
        route: '/settings/contacts',
        tab: 'contacts',
        instanceType: 'contact',
        panel: { key: 'contact' },
    },

    // ---------------------------------------------------------------- extensions
    {
        key: 'extensions.list',
        label: 'Extension Management',
        route: '/settings/extensions',
        permission: 'orgExtensionModify',
    },
    {
        key: 'extensions.configuration',
        label: 'Extension Management · Extension Configuration',
        route: '/settings/extensions',
        permission: 'orgExtensionModify',
        instanceType: 'installedExtension',
        panel: { key: 'extension', tab: 'edit' },
    },
    {
        key: 'apiKeys',
        label: 'API Keys',
        route: '/settings/apikeys',
    },
    {
        key: 'webhooks.list',
        label: 'Webhooks',
        route: '/settings/webhooks',
        permission: 'orgExtensionModify',
    },
    {
        key: 'webhooks.details',
        label: 'Webhooks · Webhook',
        route: '/settings/webhooks',
        permission: 'orgExtensionModify',
        instanceType: 'webhook',
        panel: { key: 'webhook' },
    },

    // ---------------------------------------------------------------- storefront (organization-wide)
    {
        key: 'storefront.global.dashboard',
        label: 'Storefront · Dashboard',
        route: '/settings/storefront/dashboard',
        permission: 'storefrontAdmin',
    },
    {
        key: 'storefront.global.ordering',
        label: 'Storefront · Ordering & Carts (all storefronts)',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
    },
    {
        key: 'storefront.global.ordering.email',
        label: 'Storefront · Ordering & Carts · Email Settings',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
        tab: 'email',
    },
    {
        key: 'storefront.global.ordering.questions',
        label: 'Storefront · Ordering & Carts · Order Questions (all storefronts)',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
        tab: 'questions',
    },
    {
        key: 'storefront.global.ordering.payments',
        label: 'Storefront · Ordering & Carts · Payments & Billing',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
        tab: 'payments',
    },
    {
        // The Stripe fields live behind the Configure Stripe panel on the Payments & Billing tab,
        // not on the tab itself, so they need their own area carrying the panel key.
        key: 'storefront.global.ordering.payments.stripe',
        label: 'Storefront · Ordering & Carts · Payments & Billing · Stripe Configuration',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
        tab: 'payments',
        panel: { key: 'stripeConfig' },
    },
    {
        key: 'storefront.global.ordering.shipping',
        label: 'Storefront · Ordering & Carts · Shipping',
        route: '/settings/storefront/ordering',
        permission: 'storefrontAdmin',
        tab: 'shipping',
    },
    {
        key: 'storefront.global.products.settings',
        label: 'Storefront · Products · Settings (all storefronts)',
        route: '/settings/storefront/products',
        permission: 'storefrontAdmin',
        tab: 'settings',
    },
    {
        key: 'storefront.global.products',
        label: 'Storefront · Products (all storefronts)',
        route: '/settings/storefront/products',
        permission: 'storefrontAdmin',
    },
    {
        key: 'storefront.global.products.fulfillment',
        label: 'Storefront · Products · Fulfillment',
        route: '/settings/storefront/products',
        permission: 'storefrontAdmin',
        tab: 'fulfillment',
        // Per-vendor settings live behind the edit panel opened from a vendor's row; the vendor id
        // travels inside the setting id rather than through a picker, so this only opens the panel.
        panel: { key: 'fulfillmentVendor' },
    },
    {
        key: 'storefront.global.integrations.email',
        label: 'Storefront · Integrations · Email (all storefronts)',
        route: '/settings/storefront/integrations',
        permission: 'storefrontAdmin',
        tab: 'email',
    },
    {
        key: 'storefront.global.integrations',
        label: 'Storefront · Integrations (all storefronts)',
        route: '/settings/storefront/integrations',
        permission: 'storefrontAdmin',
    },
    /*
     * One area per tab of the same page, matching the Appearance page's split - the reveal mechanism
     * opens accordions but not Pivots, so a field on the Customers or Locations tab needs its own
     * area or the link lands on the default Opportunities tab and honestly reports it cannot find it.
     */
    {
        key: 'storefront.global.integrations.customers',
        label: 'Storefront · Integrations (all storefronts) · Customers',
        route: '/settings/storefront/integrations',
        permission: 'storefrontAdmin',
        tab: 'customers',
    },
    {
        key: 'storefront.global.integrations.locations',
        label: 'Storefront · Integrations (all storefronts) · Locations',
        route: '/settings/storefront/integrations',
        permission: 'storefrontAdmin',
        tab: 'locations',
    },
    {
        key: 'storefront.global.appearance',
        label: 'Storefront · Appearance (all storefronts)',
        route: '/settings/storefront/appearance',
        permission: 'storefrontAdmin',
        tab: 'headerFooter',
    },
    /*
     * One area per tab of the same page. An area carries a single `tab`, and the reveal mechanism
     * opens accordions but not Pivots — so a field on the Banners or Legacy tab needs its own area
     * or the link lands on Header & Footer and honestly reports it cannot find the field.
     */
    {
        key: 'storefront.global.appearance.banners',
        label: 'Storefront · Appearance (all storefronts) · Banners',
        route: '/settings/storefront/appearance',
        permission: 'storefrontAdmin',
        tab: 'banners',
    },
    {
        key: 'storefront.global.appearance.legacy',
        label: 'Storefront · Appearance (all storefronts) · Legacy Settings',
        route: '/settings/storefront/appearance',
        permission: 'storefrontAdmin',
        tab: 'legacy',
    },
    {
        key: 'storefront.global.assets',
        label: 'Storefront · Assets',
        route: '/settings/storefront/assets',
        permission: 'storefrontAdmin',
    },
    {
        key: 'storefront.list',
        label: 'Storefront · Storefronts',
        route: '/settings/storefront/storefronts',
        permission: 'storefrontAdmin',
    },

    // ---------------------------------------------------------------- storefront (per storefront)
    {
        key: 'storefront.dashboard',
        label: 'Storefront · Dashboard',
        route: '/settings/storefront/storefronts/:instance/dashboard',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        globalCounterpart: 'storefront.global.dashboard',
    },
    {
        key: 'storefront.dashboard.orders',
        label: 'Storefront · Orders',
        route: '/settings/storefront/storefronts/:instance/dashboard',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'orders',
    },
    {
        key: 'storefront.dashboard.carts',
        label: 'Storefront · Customer Carts',
        route: '/settings/storefront/storefronts/:instance/dashboard',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'carts',
    },
    {
        key: 'storefront.dashboard.preparedCarts',
        label: 'Storefront · Prepared Carts',
        route: '/settings/storefront/storefronts/:instance/dashboard',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'preparedCarts',
    },
    {
        key: 'storefront.general',
        label: 'Storefront · General Settings',
        route: '/settings/storefront/storefronts/:instance/general',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
    },
    {
        key: 'storefront.pages',
        label: 'Storefront · Pages',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
    },
    /*
     * The page editors are linkable as screens rather than field by field. What they configure is
     * sections, styles and content that vary per storefront and per section, so there is no fixed
     * set of fields to enumerate - but "here is where you edit the cart page" is exactly what
     * documentation needs to point at.
     */
    {
        key: 'storefront.pages.home',
        label: 'Storefront · Pages · Home',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        description: 'Home page sections, banners and layout.',
    },
    {
        key: 'storefront.pages.search',
        label: 'Storefront · Pages · Search',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'Search results page layout.',
    },
    /*
     * The Search page editor's style bar opened its panels from local useState — the same
     * "toolbar-opened editor" bug the home page editor's Nav Pods / Affiliations panels had. Each
     * panel gets its own area so a link opens the right one; one area carries one panel, per the
     * established rule.
     */
    {
        key: 'storefront.pages.search.container',
        label: 'Storefront · Pages · Search · Container',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'The box around the results grid/list, the card gap, and (in cards view) the card style.',
        panel: { key: 'container' },
    },
    {
        key: 'storefront.pages.search.paginator',
        label: 'Storefront · Pages · Search · Paginator',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'The page navigation control beside the search bar.',
        panel: { key: 'paginator' },
    },
    {
        key: 'storefront.pages.search.breadcrumb',
        label: 'Storefront · Pages · Search · Breadcrumbs',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'The selected-filter chip row above the results, shown when facet filters are off.',
        panel: { key: 'breadcrumb' },
    },
    {
        key: 'storefront.pages.search.metaChips',
        label: 'Storefront · Pages · Search · Row Meta Chips',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'The Category and Manufacturer chips on each result row.',
        panel: { key: 'metaChips' },
    },
    {
        key: 'storefront.pages.search.filters',
        label: 'Storefront · Pages · Search · Filters',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'Shopper facet filtering: on/off, layout, price range, and the filter panel styling.',
        panel: { key: 'filters' },
    },
    {
        key: 'storefront.pages.search.searchBar',
        label: 'Storefront · Pages · Search · Search Bar',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'search',
        description: 'The keyword box above the results.',
        panel: { key: 'searchBar' },
    },
    {
        key: 'storefront.pages.product',
        label: 'Storefront · Pages · Product',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'Product details page layout.',
    },
    /*
     * Same bug, same fix, on the Product page editor's nine topical panels (page/gallery/chips/
     * description/purchase/promotions/linked/addedTogether/moreLikeThis) — each was local useState.
     */
    {
        key: 'storefront.pages.product.page',
        label: 'Storefront · Pages · Product · Page',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The box around the whole product page, and the product title heading.',
        panel: { key: 'page' },
    },
    {
        key: 'storefront.pages.product.gallery',
        label: 'Storefront · Pages · Product · Gallery',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The image column: the main image and its frame, the thumbnail carousel and its arrows.',
        panel: { key: 'gallery' },
    },
    {
        key: 'storefront.pages.product.chips',
        label: 'Storefront · Pages · Product · Chips',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The product tag chips and the attribute chips beside the image.',
        panel: { key: 'chips' },
    },
    {
        key: 'storefront.pages.product.description',
        label: 'Storefront · Pages · Product · Description',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'Whether the description collapses behind a "Show more" toggle, and its styling.',
        panel: { key: 'description' },
    },
    {
        key: 'storefront.pages.product.purchase',
        label: 'Storefront · Pages · Product · Purchase',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'Price, list price, the quantity box and the Add to Cart control.',
        panel: { key: 'purchase' },
    },
    {
        key: 'storefront.pages.product.promotions',
        label: 'Storefront · Pages · Product · Promotions',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The promo pill, original price line and end date, shown only while a promotion prices the product.',
        panel: { key: 'promotions' },
    },
    {
        key: 'storefront.pages.product.linked',
        label: 'Storefront · Pages · Product · Linked Items',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The linked-products section below the description: visibility and styling.',
        panel: { key: 'linked' },
    },
    {
        key: 'storefront.pages.product.addedTogether',
        label: 'Storefront · Pages · Product · Added Together',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The "frequently added together" row: visibility and styling.',
        panel: { key: 'addedTogether' },
    },
    {
        key: 'storefront.pages.product.moreLikeThis',
        label: 'Storefront · Pages · Product · More Like This',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'product',
        description: 'The similar-products row: visibility and styling.',
        panel: { key: 'moreLikeThis' },
    },
    {
        key: 'storefront.pages.bundle',
        label: 'Storefront · Pages · Bundle',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'bundle',
        description: 'Bundle page layout.',
    },
    {
        key: 'storefront.pages.cart',
        label: 'Storefront · Pages · Cart',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'cart',
        description: 'Cart page layout.',
    },
    {
        key: 'storefront.pages.checkout',
        label: 'Storefront · Pages · Checkout',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'checkout',
        description: 'Checkout page layout.',
    },
    {
        key: 'storefront.pages.login',
        label: 'Storefront · Pages · Sign In',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'login',
        description: 'Sign-in and sign-up page appearance.',
    },
    {
        key: 'storefront.pages.styles',
        label: 'Storefront · Pages · Storefront Styles',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        description: "Storefront-wide colours and fonts, edited in place from the page editor's style bar.",
    },
    {
        key: 'storefront.pages.header',
        label: 'Storefront · Pages · Header Bar',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'headerFooter',
        description: 'Header bar colors and font, edited in place on the page editor.',
    },
    {
        /*
         * The gear-icon panel on the custom header links editor (alignment + the default container/
         * cell styling) was gated by local useState — same "toolbar-opened editor" bug. Split out
         * from storefront.pages.header, which also carries the Header Bar style picker's ids
         * (storefront.header.<styleKey>) that are edited in place, not behind a panel — adding a
         * panel key there would have forced those links to open a panel that doesn't hold them.
         */
        key: 'storefront.pages.headerLinks',
        label: 'Storefront · Pages · Header Links',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'headerFooter',
        description: 'Custom header links: alignment, and the default container/cell styling shared by every link.',
        panel: { key: 'headerLinks' },
    },
    {
        key: 'storefront.pages.headerFooter',
        label: 'Storefront · Pages · Header & Footer',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'headerFooter',
        description: 'Header and footer content, links and styling.',
    },
    {
        key: 'storefront.pages.headerSettings',
        label: 'Storefront · Pages · Header Settings',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'headerFooter',
        description: 'Sticky header, the search row, and the header bar height.',
        // Its fields live in a toolbar flyout, so the link has to open it.
        panel: { key: 'headerSettings' },
    },
    {
        key: 'storefront.section.style',
        label: 'Storefront · Pages · Section Style',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        panel: { key: 'sectionStyle' },
        description:
            'Background and border of one section. Opened from a section\'s Style menu; a link must name the section as well as the storefront.',
    },
    {
        key: 'storefront.section.layout',
        label: 'Storefront · Pages · Section Layout',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        panel: { key: 'sectionLayout' },
        description: 'Padding, margin and dimensions of one section.',
    },
    {
        key: 'storefront.section.appearance',
        label: 'Storefront · Pages · Section Appearance',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        panel: { key: 'sectionAppearance' },
        description:
            "A section's Section / Header / Footer / Product Cards styling, in the page editor's Appearance tab. Needs a section as well as a storefront, so link the screen generically and copy a specific link from the highlight.",
    },
    {
        key: 'storefront.pages.affiliations',
        label: 'Storefront · Pages · Affiliations / Awards',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        description: 'The affiliations and awards strip on the home page, and the styling of its band, tiles, images and text.',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'affiliations' },
    },
    {
        key: 'storefront.pages.navPods',
        label: 'Storefront · Pages · Navigation Pods',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        description: 'The row of navigation tiles on the home page, and the styling shared by every tile.',
        panel: { key: 'navPods' },
    },
    {
        key: 'storefront.pages.notifications',
        label: 'Storefront · Pages · Notification Bars',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'headerFooter',
        panel: { key: 'notifications' },
    },
    {
        key: 'storefront.pages.productCards',
        label: 'Storefront · Pages · Product Cards',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'productCards' },
    },
    {
        key: 'storefront.pages.categoryPods',
        label: 'Storefront · Pages · Category Pods',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'categoryPods' },
    },
    {
        key: 'storefront.pages.browseAll',
        label: 'Storefront · Pages · Browse All Band',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'browseAll' },
    },
    {
        key: 'storefront.pages.searchSuggestions',
        label: 'Storefront · Pages · Search Suggestions',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'searchSuggestions' },
    },
    {
        key: 'storefront.pages.loadingSpinner',
        label: 'Storefront · Pages · Loading Spinner',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        // Its fields live in a toolbar panel, so the link has to open it.
        panel: { key: 'loadingSpinner' },
    },
    {
        /*
         * The pod STYLING lives on the panel's Style pivot, not its Pods pivot, so it needs its own
         * area — one area carries one panel tab. Without this the style ids resolved to the home
         * page with no panel at all and reported they could not be found.
         */
        key: 'storefront.pages.navPods.style',
        label: 'Storefront · Pages · Navigation Pods · Style',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        panel: { key: 'navPods', tab: 'style' },
    },
    {
        key: 'storefront.pages.managePages',
        label: 'Storefront · Pages · Manage Custom Pages',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'home',
        description: 'Add, rename, enable/disable or delete custom pages served at {domain}/{slug}.',
        panel: { key: 'managePages' },
    },
    {
        key: 'storefront.pages.banners',
        label: 'Storefront · Pages · Banners',
        route: '/settings/storefront/storefronts/:instance/pages',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        panel: { key: 'banners' },
        description: 'The banner carousel for a page: its slides, and the carousel settings that apply to the whole area.',
    },
    {
        key: 'storefront.localization',
        label: 'Storefront · Localization',
        route: '/settings/storefront/storefronts/:instance/localization',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        description:
            'Overrides for the storefront\'s wording. Linkable as a screen rather than per string: the editor lists only keys that already have an override or match the current context filter.',
    },
    {
        key: 'storefront.ordering',
        label: 'Storefront · Ordering & Carts',
        route: '/settings/storefront/storefronts/:instance/ordering',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        globalCounterpart: 'storefront.global.ordering',
    },
    {
        key: 'storefront.ordering.email',
        label: 'Storefront · Ordering & Carts · Email Settings',
        route: '/settings/storefront/storefronts/:instance/ordering',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'email',
    },
    {
        key: 'storefront.ordering.questions',
        label: 'Storefront · Ordering & Carts · Order Questions',
        route: '/settings/storefront/storefronts/:instance/ordering',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'questions',
    },
    {
        key: 'storefront.ordering.quotes',
        label: 'Storefront · Ordering & Carts · Quote Requests',
        route: '/settings/storefront/storefronts/:instance/ordering',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'quotes',
    },
    {
        key: 'storefront.products',
        label: 'Storefront · Products',
        route: '/settings/storefront/storefronts/:instance/products',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
    },
    {
        key: 'storefront.integrations.opportunities',
        label: 'Storefront · Integrations · Opportunities',
        route: '/settings/storefront/storefronts/:instance/integrations',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'opportunities',
        description: 'Opportunity extension overrides for this storefront: checkout and quote-request opportunity creation.',
    },
    {
        key: 'storefront.integrations.customers',
        label: 'Storefront · Integrations · Customers',
        route: '/settings/storefront/storefronts/:instance/integrations',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'customers',
    },
    {
        key: 'storefront.integrations.locations',
        label: 'Storefront · Integrations · Locations',
        route: '/settings/storefront/storefronts/:instance/integrations',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'locations',
    },
    {
        key: 'storefront.integrations.tax',
        label: 'Storefront · Integrations · Tax',
        route: '/settings/storefront/storefronts/:instance/integrations',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'tax',
    },
    {
        key: 'storefront.integrations',
        label: 'Storefront · Integrations',
        route: '/settings/storefront/storefronts/:instance/integrations',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        globalCounterpart: 'storefront.global.integrations',
    },
    {
        key: 'storefront.authentication.login',
        label: 'Storefront · Authentication · Login',
        route: '/settings/storefront/storefronts/:instance/authentication',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'login',
    },
    {
        key: 'storefront.authentication.signup',
        label: 'Storefront · Authentication · Signup',
        route: '/settings/storefront/storefronts/:instance/authentication',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
        tab: 'signup',
    },
    {
        key: 'storefront.authentication',
        label: 'Storefront · Authentication',
        route: '/settings/storefront/storefronts/:instance/authentication',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
    },
    {
        key: 'storefront.users',
        label: 'Storefront · Users',
        route: '/settings/storefront/storefronts/:instance/users',
        permission: 'storefrontAdmin',
        instanceType: 'storefront',
    },
] as const;

/** Fails to compile if any entry above is malformed. */
export type _AreasAreValid = CheckAreas<typeof AREAS>;

/** Union of every declared area key, used to type-check the `area` on each setting. */
export type SettingAreaKey = (typeof AREAS)[number]['key'];
